import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { Modal, ModalTitle, ModalFooter, ModalBody } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { client } from '../../api';
import { AuthContext } from '../../auth';
import SaveFiltersTable from './SaveFiltersTable';
import { applySavedFilters } from '../../utils';

const CustomModalBody = styled(ModalBody)`
  padding-top: 8px;
  padding-bottom: 0;
  gap: 0;
`;

const InlineButton = styled(Button)`
  display: inline;
`;

export type SaveFiltersModalProps = {
  onClose: () => void;
  filters: Array<{ label: string; displayValues: string[]; saveValues: string[] }>;
};

const SaveFiltersModal = ({ onClose, filters }: SaveFiltersModalProps) => {
  const authContext = React.useContext(AuthContext);
  const queryClient = useQueryClient();
  // Extract prescriber filter
  const prescriberFilter = filters.filter((f) => f.label === 'Prescriber');
  const prescriberFilterSave = prescriberFilter.flatMap((f) => f.saveValues);
  const prescriberFilterDisplay = prescriberFilter.flatMap((f) => f.displayValues);

  const saveFiltersMutation = useMutation({
    mutationFn: async () => {
      return client
        .post(`notification/settings/save-filters`, {
          npis: prescriberFilterSave,
        })
        .then((res) => res.data);
    },
    onSuccess: (data) => {
      if (authContext.user) {
        // Use the prescriber names returned from the backend
        const savedPrescriberNames = data.saved_prescriber_names || [];
        const providersWithNames = prescriberFilterSave.map((npi, index) => ({
          npi,
          name: savedPrescriberNames[index] || npi, // fallback to NPI if name not found
        }));

        applySavedFilters(authContext, providersWithNames, queryClient);
      }
      onClose();
    },
  });

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Would you like to save the selected filters for future browsing?</ModalTitle>

      <CustomModalBody>
        <Typography styledAs="bodySpaceGrotesk">
          You can make changes to these preferences in{' '}
          <InlineButton variant="link" to="/settings">
            Settings
          </InlineButton>
          <span aria-hidden>.</span>
        </Typography>
        <SaveFiltersTable prescriberNames={prescriberFilterDisplay} />
      </CustomModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={onClose} disabled={saveFiltersMutation.isPending}>
          Cancel
        </Button>
        <Button onClick={() => saveFiltersMutation.mutate()} isLoading={saveFiltersMutation.isPending}>
          Save default filters
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SaveFiltersModal;
